import React from 'react';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Card } from '../ui/card';
import { Building2, Layers } from 'lucide-react';

const Step2BuildingCharacteristics = ({ formData, updateFormData }) => {
  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Building Characteristics</h2>
        <p className="text-gray-600">These factors affect heating and cooling requirements</p>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="insulationQuality" className="text-base font-medium">
          Insulation Quality
        </Label>
        <Select value={formData.insulationQuality} onValueChange={(value) => updateFormData('insulationQuality', value)}>
          <SelectTrigger className="h-12 text-lg border-blue-200 focus:border-blue-500 focus:ring-blue-500">
            <SelectValue placeholder="Select insulation quality" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="poor">
              Poor - No insulation, drafty
            </SelectItem>
            <SelectItem value="average">
              Average - Basic insulation
            </SelectItem>
            <SelectItem value="good">
              Good - Modern insulation
            </SelectItem>
            <SelectItem value="excellent">
              Excellent - High-performance insulation
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-sm text-gray-500 mt-1">
          {formData.insulationQuality === 'poor' && 'Single-pane windows, no wall insulation, air leaks'}
          {formData.insulationQuality === 'average' && 'Standard construction, some insulation'}
          {formData.insulationQuality === 'good' && 'Double-glazed windows, proper wall insulation'}
          {formData.insulationQuality === 'excellent' && 'Triple-glazed windows, high R-value insulation'}
        </p>
      </div>
      
      <div className="space-y-3">
        <Label className="text-base font-medium">
          Number of External Walls
        </Label>
        <RadioGroup 
          value={formData.externalWalls.toString()} 
          onValueChange={(value) => updateFormData('externalWalls', parseInt(value))}
          className="grid grid-cols-2 sm:grid-cols-4 gap-3"
        >
          {[1, 2, 3, 4].map((num) => (
            <Card 
              key={num}
              className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                formData.externalWalls === num 
                  ? 'border-blue-700 bg-blue-50 shadow-md' 
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <label className="flex items-center justify-center p-4 cursor-pointer">
                <RadioGroupItem value={num.toString()} id={`walls-${num}`} className="sr-only" />
                <div className="text-center">
                  <Building2 className={`w-8 h-8 mx-auto mb-2 ${
                    formData.externalWalls === num ? 'text-blue-700' : 'text-gray-400'
                  }`} />
                  <span className={`font-medium ${
                    formData.externalWalls === num ? 'text-blue-900' : 'text-gray-700'
                  }`}>{num} Wall{num > 1 ? 's' : ''}</span>
                </div>
              </label>
            </Card>
          ))}
        </RadioGroup>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="buildingAge" className="text-base font-medium">
          Building Age
        </Label>
        <Select value={formData.buildingAge} onValueChange={(value) => updateFormData('buildingAge', value)}>
          <SelectTrigger className="h-12 text-lg border-blue-200 focus:border-blue-500 focus:ring-blue-500">
            <SelectValue placeholder="Select building age" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="new">New Construction (0-5 years)</SelectItem>
            <SelectItem value="5-15">5-15 years old</SelectItem>
            <SelectItem value="15-30">15-30 years old</SelectItem>
            <SelectItem value="30+">30+ years old</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="floorLocation" className="text-base font-medium">
          Floor Location
        </Label>
        <Select value={formData.floorLocation} onValueChange={(value) => updateFormData('floorLocation', value)}>
          <SelectTrigger className="h-12 text-lg border-blue-200 focus:border-blue-500 focus:ring-blue-500">
            <SelectValue placeholder="Select floor location" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ground">Ground Floor</SelectItem>
            <SelectItem value="upper">Upper Floor</SelectItem>
            <SelectItem value="basement">Basement</SelectItem>
            <SelectItem value="top">Top Floor (directly under roof)</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default Step2BuildingCharacteristics;